import {
  I2cRadioButton,
  I2cCheckbox,
  I2cCheckboxGroup,
  I2cRadioGroup,
  I2cListItem,
  I2cInput,
} from "@webcomponents/i2cwebcomponents/dist/react";
import "../../PrepaidComponents/Prepaid.scss";
import React, { useEffect } from "react";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import ErrorMessage from "../../../Components/SharedComponent/ErrorMessage/ErrorMessage";
import { agentWebsiteLang, webRequire } from "./stringUtils";
import { handleCheckboxArrayToggle, handleInputChange, handleRadioChange } from "./formUtils";
import { validationSchemaBulkCardOrderingPortal } from "./ValidationSchema";
 
const BulkCardOrderingPortal = ({
  setAddFormData,
  addFormData,
  setFormValidStates,
  formDataJson,
  isFetching,
}) => {
  // Initialize React Hook Form with Yup resolver
  const methods = useForm({
    resolver: yupResolver(validationSchemaBulkCardOrderingPortal),
    mode: "all",
    reValidateMode: "all",
    shouldUseNativeValidation: false,
    criteriaMode: "all",
    defaultValues: {
      bulk_card_ordering_portal_would_you_like_c_partner: "no",
      bulk_card_ordering_portal_c_partner_language: [],
      bulk_card_ordering_portal_c_partner_language_other: "",
      bulk_card_ordering_portal_url: "",
    },
  });

  const {
    formState: { errors },
  } = methods;

  const fields = [
    "bulk_card_ordering_portal_would_you_like_c_partner",
    "bulk_card_ordering_portal_c_partner_language",
    "bulk_card_ordering_portal_c_partner_language_other",
    "bulk_card_ordering_portal_url",
  ];

  // Handle value changes and update parent state
  const handleValueChanges = React.useCallback(() => {
    const values = methods.getValues();
    setAddFormData({
      ...addFormData,
      3: {
        ...addFormData["3"],
        "3_7": { ...values },
      },
    });
  }, [methods, setAddFormData, addFormData]);

  // Load existing form data
  useEffect(() => {
    if (!isFetching && Object.keys(formDataJson ?? {}).length) {
      fields.forEach((field) => {
        const value = formDataJson[3]?.["3_7"]?.[field];
        if (value !== undefined && value !== null) {
          methods.setValue(field, value);
        }
      });
      methods.trigger();
      handleValueChanges();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isFetching]);

  // Trigger validation when component mounts
  useEffect(() => {
    methods.trigger();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Update form validation state when errors change
  useEffect(() => {
    const errorsLength = Object.keys(errors)?.length;
    if (errorsLength === 0) {
      setFormValidStates("3_7", true);
    } else {
      setFormValidStates("3_7", false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [errors]);

  const isWebRequired = methods.watch("bulk_card_ordering_portal_would_you_like_c_partner") === "yes";

  return (
    <React.Fragment>
      <div className="forms-instance">
        <div i2c-grid="grid gap-md">
          <div i2c-grid="12">
            <div className="section-space">
              <div className="sub-heading mb-16">Would you like C-Partner?</div>
              <I2cRadioGroup
                label=""
                value={methods.watch("bulk_card_ordering_portal_would_you_like_c_partner")}
                name="bulk_card_ordering_portal_would_you_like_c_partner"
                onI2cChange={(e) => {
                  const value = String(e.target.value);

                  handleRadioChange(
                    methods,
                    "bulk_card_ordering_portal_would_you_like_c_partner",
                    value,
                    value !== "yes" ? {
                      bulk_card_ordering_portal_c_partner_language: [],
                      bulk_card_ordering_portal_c_partner_language_other: "",
                      bulk_card_ordering_portal_url: "",
                    } : {},
                    handleValueChanges
                  );
                }}
              >
                {webRequire.map((option) => (
                  <I2cRadioButton key={option.value} value={option.value}>
                    {option.label}
                  </I2cRadioButton>
                ))}
              </I2cRadioGroup>
              {errors.bulk_card_ordering_portal_would_you_like_c_partner && (
                <ErrorMessage show={true} message={errors.bulk_card_ordering_portal_would_you_like_c_partner.message} />
              )}
            </div>
            {isWebRequired && (
              <>
                <div className="section-space">
                  <div className="sub-heading mb-16">C-Partner Language</div>
                  <I2cCheckboxGroup
                    key={`bulk-card-language-${methods.watch("bulk_card_ordering_portal_would_you_like_c_partner")}`}
                  >
                    <div i2c-grid="grid gap-sm">
                      {agentWebsiteLang.map((web, index) => (
                        <I2cListItem
                          key={index}
                          i2c-grid="4@md"
                          variant="receipt"
                          label={web.label}
                          value=""
                          type="checkbox"
                          checked={methods.watch("bulk_card_ordering_portal_c_partner_language").includes(web.value)}
                          onI2cCheckboxClicked={() =>
                            handleCheckboxArrayToggle(
                              methods,
                              "bulk_card_ordering_portal_c_partner_language",
                              web.value,
                              { dependentField: "bulk_card_ordering_portal_c_partner_language_other" },
                              handleValueChanges
                            )
                          }
                        >
                          <I2cCheckbox
                            slot="prefix"
                            name="bulk_card_ordering_portal_c_partner_language"
                            value={web.value}
                            label=""
                            filled
                            checked={methods.watch("bulk_card_ordering_portal_c_partner_language").includes(web.value)}
                            className="customCheckbox"
                          ></I2cCheckbox>
                        </I2cListItem>
                      ))}
                    </div>
                  </I2cCheckboxGroup>
                  {errors.bulk_card_ordering_portal_c_partner_language && (
                    <ErrorMessage show={true} message={errors.bulk_card_ordering_portal_c_partner_language.message} />
                  )}
                  {methods.watch("bulk_card_ordering_portal_c_partner_language").includes("other") && (
                    <div i2c-grid="grid gap-sm" className="mt-16">
                      <div i2c-grid="4@md">
                        <div className="single-field-wrapper">
                          <I2cInput
                            className="single-input-field"
                            placeholder="Please Specify"
                            size="large"
                            label="Enter Details"
                            name="bulk_card_ordering_portal_c_partner_language_other"
                            value={methods.watch("bulk_card_ordering_portal_c_partner_language_other")}
                            onI2cInput={(e) => 
                              handleInputChange(methods, "bulk_card_ordering_portal_c_partner_language_other", e.target.value, handleValueChanges)
                            }
                            invalid={errors.bulk_card_ordering_portal_c_partner_language_other ? true : false}
                          />
                          {errors.bulk_card_ordering_portal_c_partner_language_other && (
                            <ErrorMessage show={true} message={errors.bulk_card_ordering_portal_c_partner_language_other.message} />
                          )}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
                {/* <div i2c-grid="grid gap-sm" className="mt-16">
                  <div i2c-grid="4@md">
                    <div className="single-field-wrapper">
                      <I2cInput
                        label="URL"
                        className="single-input-field"
                        onI2cInput={(e) => 
                          handleInputChange(methods, "bulk_card_ordering_portal_url", e.target.value, handleValueChanges)
                        }
                        value={methods.watch("bulk_card_ordering_portal_url")}
                        placeholder="www.cpartner.com"
                        name="bulk_card_ordering_portal_url"
                        size="large"
                        invalid={errors.bulk_card_ordering_portal_url ? true : false}
                      />
                      {errors.bulk_card_ordering_portal_url && (
                        <ErrorMessage show={true} message={errors.bulk_card_ordering_portal_url?.message || "Please enter a valid URL"} />
                      )}
                    </div>
                  </div>
                </div> */}
              </>
            )}
          </div>
        </div>
      </div>
    </React.Fragment>
  );
};

export default BulkCardOrderingPortal;
